import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import {
  Check,
  CheckCircle2,
  Clock,
  Pencil,
  Play,
  Plus,
  Sparkles,
  Trash2,
  Workflow as WorkflowIcon,
  XCircle,
} from 'lucide-react'
import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { MobileBottomSlots, useMobilePageTitle } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import {
  api,
  type CreateInvoiceFromEntriesConfig,
  type Workflow,
  type WorkflowPeriodStrategy,
  type WorkflowScheduleConfig,
  type WorkflowScheduleKind,
} from '../lib/api'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/workflows',
  component: WorkflowsPage,
})

const WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag']

const MONTHS = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
]

const PERIOD_LABELS: Record<WorkflowPeriodStrategy, string> = {
  last_week: 'Letzte Woche',
  last_month: 'Letzter Monat',
  last_quarter: 'Letztes Quartal',
  last_year: 'Letztes Jahr',
  current_month: 'Aktueller Monat',
  current_year: 'Aktuelles Jahr',
}

function WorkflowsPage() {
  const qc = useQueryClient()
  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })

  // Refetch every 30s so the next-run + last-run columns stay fresh while
  // the scheduler ticks in the background.
  const { data, isLoading } = useQuery({
    queryKey: ['workflows'],
    queryFn: () => api.listWorkflows(),
    enabled: !!meData?.user,
    refetchInterval: 30_000,
  })
  const workflows = data?.workflows ?? []

  const [dialogState, setDialogState] = useState<{ open: boolean; workflow: Workflow | null }>({
    open: false,
    workflow: null,
  })
  // Mobile-Shell (<640): Titel oben, ＋ Neuer Workflow unten.
  useMobilePageTitle('Workflows')

  const runMutation = useMutation({
    mutationFn: (id: string) => api.runWorkflow(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['workflows'] })
      if (r.run.status === 'success') {
        const result = r.run.result as { skipped?: boolean; reason?: string } | null
        if (result?.skipped) notify.info(`Übersprungen: ${result.reason}`)
        else notify.success('Workflow erfolgreich ausgeführt')
      } else if (r.run.status === 'failed') {
        notify.error(`Workflow fehlgeschlagen: ${r.run.error}`)
      }
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.updateWorkflow(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workflows'] }),
    onError: (err: Error) => notify.error(err.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteWorkflow(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflows'] })
      notify.success('Workflow gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): ＋ Neuer Workflow — derselbe Handler
          wie der Kopf-Button. */}
      <MobileBottomSlots>
        <BarAccentPlus
          ariaLabel="Neuer Workflow"
          onClick={() => setDialogState({ open: true, workflow: null })}
        />
      </MobileBottomSlots>

      {/* In-Content-Kopf: <640 übernimmt die Shell (Titel oben, Aktionen
          unten) — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <PageHeader
        title="Workflows"
        actions={
          <Button onClick={() => setDialogState({ open: true, workflow: null })} size="lg">
            <Plus className="h-4 w-4" /> Neuer Workflow
          </Button>
        }
      />

      {isLoading ? (
        <p className="text-text-tertiary">Lade …</p>
      ) : workflows.length === 0 ? (
        <EmptyState onCreate={() => setDialogState({ open: true, workflow: null })} />
      ) : (
        <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {workflows.map((w) => (
            <li key={w.id}>
              <WorkflowCard
                workflow={w}
                onEdit={() => setDialogState({ open: true, workflow: w })}
                onRun={() => runMutation.mutate(w.id)}
                onToggle={() => toggleMutation.mutate({ id: w.id, enabled: !w.enabled })}
                onDelete={async () => {
                  const ok = await confirm({
                    title: `„${w.name}“ löschen?`,
                    description: 'Inklusive aller Run-Logs. Nicht rückgängig.',
                    variant: 'danger',
                    confirmLabel: 'Löschen',
                  })
                  if (ok) deleteMutation.mutate(w.id)
                }}
                running={runMutation.isPending}
              />
            </li>
          ))}
        </ul>
      )}

      {dialogState.open && (
        <WorkflowDialog
          workflow={dialogState.workflow}
          onClose={() => setDialogState({ open: false, workflow: null })}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['workflows'] })
            setDialogState({ open: false, workflow: null })
          }}
        />
      )}
    </div>
  )
}

// ============================================================================
// Workflow card
// ============================================================================

function WorkflowCard({
  workflow,
  onEdit,
  onRun,
  onToggle,
  onDelete,
  running,
}: {
  workflow: Workflow
  onEdit: () => void
  onRun: () => void
  onToggle: () => void
  onDelete: () => void
  running: boolean
}) {
  return (
    <article
      className={cn(
        'group rounded-2xl border bg-bg-elevated/40 p-4 transition-colors',
        workflow.enabled ? 'border-border-subtle' : 'border-border-subtle opacity-60',
      )}
    >
      <header className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <WorkflowIcon className="h-4 w-4 text-accent shrink-0" />
            <h3 className="text-base font-medium text-text-primary truncate" title={workflow.name}>
              {workflow.name}
            </h3>
            {!workflow.enabled && (
              <span className="text-2xs uppercase tracking-wide text-text-tertiary px-1.5 py-0.5 rounded bg-bg-overlay">
                pausiert
              </span>
            )}
          </div>
          {workflow.description && (
            <p className="mt-0.5 text-xs text-text-tertiary truncate" title={workflow.description}>
              {workflow.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity">
          <IconButton onClick={onEdit} title="Bearbeiten" aria-label="Bearbeiten">
            <Pencil className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton onClick={onDelete} title="Löschen" aria-label="Löschen">
            <Trash2 className="h-3.5 w-3.5" />
          </IconButton>
        </div>
      </header>

      <div className="space-y-1.5 text-sm">
        <div className="flex items-center gap-2 text-text-secondary">
          <Clock className="h-3.5 w-3.5 text-text-tertiary" />
          <span>{describeSchedule(workflow.schedule, workflow.scheduleConfig)}</span>
        </div>
        <div className="flex items-center gap-2 text-text-secondary">
          <Sparkles className="h-3.5 w-3.5 text-text-tertiary" />
          <span>{describeAction(workflow.actionType, workflow.actionConfig)}</span>
        </div>
      </div>

      <footer className="mt-3 pt-3 border-t border-border-subtle flex items-center justify-between gap-3">
        <div className="text-[11px] text-text-tertiary space-y-0.5 min-w-0">
          {workflow.lastRunAt && (
            <p className="flex items-center gap-1.5 truncate">
              {workflow.lastRunStatus === 'success' ? (
                <CheckCircle2 className="h-3 w-3 text-success" />
              ) : (
                <XCircle className="h-3 w-3 text-danger" />
              )}
              <span>
                Zuletzt: {formatDateTime(workflow.lastRunAt)}
                {workflow.lastRunStatus === 'failed' && workflow.lastRunError && (
                  <span className="text-danger ml-1">— {workflow.lastRunError}</span>
                )}
              </span>
            </p>
          )}
          {workflow.nextRunAt && (
            <p className="truncate">Nächster Lauf: {formatDateTime(workflow.nextRunAt)}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant="ghost" onClick={onToggle}>
            {workflow.enabled ? 'Pausieren' : 'Aktivieren'}
          </Button>
          <Button size="sm" onClick={onRun} disabled={running}>
            <Play className="h-3.5 w-3.5" /> Jetzt ausführen
          </Button>
        </div>
      </footer>
    </article>
  )
}

function IconButton({
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { children: React.ReactNode }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="text-text-tertiary hover:text-accent"
      {...rest}
    >
      {children}
    </Button>
  )
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="card-elevated px-6 py-16 text-center">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10 text-accent">
        <WorkflowIcon className="h-7 w-7" strokeWidth={1.5} />
      </div>
      <p className="mt-4 text-base font-medium text-text-primary">Noch keine Workflows</p>
      <p className="mt-1 text-sm text-text-tertiary max-w-md mx-auto">
        Beispiele: jeden Montag einen Rechnungsentwurf aus Vorwoche bauen, jeden 10. eine
        Monatsrechnung erstellen.
      </p>
      <Button onClick={onCreate} className="mt-5">
        <Plus className="h-4 w-4" /> Workflow anlegen
      </Button>
    </div>
  )
}

// ============================================================================
// Workflow editor dialog
// ============================================================================

function WorkflowDialog({
  workflow,
  onClose,
  onSaved,
}: {
  workflow: Workflow | null
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = !!workflow

  const [name, setName] = useState(workflow?.name ?? '')
  const [description, setDescription] = useState(workflow?.description ?? '')
  const [schedule, setSchedule] = useState<WorkflowScheduleKind>(workflow?.schedule ?? 'monthly')
  const [time, setTime] = useState<string>(workflow?.scheduleConfig.time ?? '09:00')
  const [weekday, setWeekday] = useState<number>(workflow?.scheduleConfig.weekday ?? 1)
  const [dayOfMonth, setDayOfMonth] = useState<number>(workflow?.scheduleConfig.dayOfMonth ?? 10)
  const [month, setMonth] = useState<number>(workflow?.scheduleConfig.month ?? 12)

  // Action config (only one action type for now)
  const initialAction = (workflow?.actionConfig ?? {}) as Partial<CreateInvoiceFromEntriesConfig>
  const [customerId, setCustomerId] = useState<string>(initialAction.customerId ?? '')
  const [projectIds, setProjectIds] = useState<string[]>(initialAction.projectIds ?? [])
  const [userIds, setUserIds] = useState<string[]>(initialAction.userIds ?? [])
  const [period, setPeriod] = useState<WorkflowPeriodStrategy>(initialAction.period ?? 'last_month')
  const [billableOnly, setBillableOnly] = useState<boolean>(initialAction.billableOnly ?? true)
  const [excludeAlreadyBilled, setExcludeAlreadyBilled] = useState<boolean>(
    initialAction.excludeAlreadyBilled ?? true,
  )
  const [taxRatePercent, setTaxRatePercent] = useState<string>(
    initialAction.taxRatePercent != null ? String(initialAction.taxRatePercent) : '19',
  )

  const { data: customersData } = useQuery({
    queryKey: ['customers', { archived: false }],
    queryFn: () => api.listCustomers(false),
  })
  const customers = customersData?.customers ?? []

  const { data: projectsData } = useQuery({
    queryKey: ['projects', { archived: false, customerId }],
    queryFn: () => api.listProjects({ customerId: customerId || undefined }),
    enabled: !!customerId,
  })
  const projects = projectsData?.projects ?? []

  const { data: usersData } = useQuery({
    queryKey: ['users-manageable'],
    queryFn: () => api.listManageableUsers(),
  })
  const users = usersData?.users ?? []

  // Clear stale project IDs when customer changes
  useEffect(() => {
    if (!customerId) return
    setProjectIds((prev) => prev.filter((id) => projects.some((p) => p.id === id)))
  }, [customerId, projects])

  const save = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Name fehlt')
      if (!customerId) throw new Error('Kunde auswählen')
      const scheduleConfig: WorkflowScheduleConfig = {}
      if (schedule !== 'manual') scheduleConfig.time = time
      if (schedule === 'weekly') scheduleConfig.weekday = weekday
      if (schedule === 'monthly') scheduleConfig.dayOfMonth = dayOfMonth
      if (schedule === 'yearly') {
        scheduleConfig.month = month
        scheduleConfig.dayOfMonth = dayOfMonth
      }
      const actionConfig: CreateInvoiceFromEntriesConfig = {
        customerId,
        projectIds: projectIds.length > 0 ? projectIds : undefined,
        userIds: userIds.length > 0 ? userIds : undefined,
        period,
        billableOnly,
        excludeAlreadyBilled,
        taxRatePercent: Number(taxRatePercent) || 19,
      }
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        schedule,
        scheduleConfig,
        actionType: 'create_invoice_from_time_entries' as const,
        actionConfig: actionConfig as unknown as Record<string, unknown>,
        enabled: workflow?.enabled ?? true,
      }
      if (isEdit && workflow) return api.updateWorkflow(workflow.id, payload)
      return api.createWorkflow(payload)
    },
    onSuccess: () => {
      notify.success(isEdit ? 'Workflow aktualisiert' : 'Workflow angelegt')
      onSaved()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    save.mutate()
  }

  const dayOfMonthOptions = useMemo(() => Array.from({ length: 31 }, (_, i) => i + 1), [])

  return (
    <SidePanel
      open
      onClose={onClose}
      title={isEdit ? 'Workflow bearbeiten' : 'Neuer Workflow'}
      description="In 4 Schritten zusammenklicken: benennen, wann, was, für welche Daten."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button onClick={onSubmit} disabled={save.isPending || !name.trim() || !customerId}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-6 pb-2">
        {/* Step 1 — Identity */}
        <section className="space-y-3">
          <h3 className="text-xs uppercase tracking-wider text-text-tertiary font-medium">
            1. Name
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="wf-name">Workflow-Name</Label>
              <Input
                id="wf-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="z.B. Monatsrechnung Karaloon"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="wf-desc">Beschreibung (optional)</Label>
              <Input
                id="wf-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Worum geht es?"
              />
            </div>
          </div>
        </section>

        {/* Step 2 — Schedule */}
        <section className="space-y-3">
          <h3 className="text-xs uppercase tracking-wider text-text-tertiary font-medium">
            2. Wann?
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {(['manual', 'daily', 'weekly', 'monthly', 'yearly'] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                onClick={() => setSchedule(kind)}
                className={cn(
                  'rounded-xl border px-3 py-2.5 text-sm transition-colors',
                  schedule === kind
                    ? 'border-accent bg-accent/10 text-accent ring-1 ring-accent/40'
                    : 'border-border-subtle bg-bg-overlay/40 text-text-secondary hover:border-border-default',
                )}
              >
                {scheduleLabel(kind)}
              </button>
            ))}
          </div>

          {schedule !== 'manual' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
              {schedule === 'weekly' && (
                <div className="space-y-1.5">
                  <Label>Wochentag</Label>
                  <Select value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
                    {WEEKDAYS.map((day, idx) => (
                      <option key={day} value={idx}>
                        {day}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              {(schedule === 'monthly' || schedule === 'yearly') && (
                <div className="space-y-1.5">
                  <Label>Tag</Label>
                  <Select
                    value={dayOfMonth}
                    onChange={(e) => setDayOfMonth(Number(e.target.value))}
                  >
                    {dayOfMonthOptions.map((d) => (
                      <option key={d} value={d}>
                        {d}.
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              {schedule === 'yearly' && (
                <div className="space-y-1.5">
                  <Label>Monat</Label>
                  <Select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                    {MONTHS.map((m, idx) => (
                      <option key={m} value={idx + 1}>
                        {m}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Uhrzeit</Label>
                <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
              </div>
            </div>
          )}
        </section>

        {/* Step 3 — Action */}
        <section className="space-y-3">
          <h3 className="text-xs uppercase tracking-wider text-text-tertiary font-medium">
            3. Was soll passieren?
          </h3>
          <div className="rounded-xl border border-accent/40 bg-accent/5 p-3">
            <div className="flex items-start gap-2">
              <Check className="h-4 w-4 text-accent shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-text-primary">
                  Rechnungsentwurf aus Zeiteinträgen erstellen
                </p>
                <p className="text-xs text-text-tertiary mt-0.5">
                  Sammelt abrechenbare Stunden im gewählten Zeitraum und erzeugt einen Draft.
                  Weitere Aktionen kommen.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Step 4 — Data filters */}
        <section className="space-y-3">
          <h3 className="text-xs uppercase tracking-wider text-text-tertiary font-medium">
            4. Für welche Daten?
          </h3>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Kunde *</Label>
              <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                <option value="">— Kunde wählen —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Projekte (leer = alle des Kunden)</Label>
              <MultiSelect
                items={projects.map((p) => ({ id: p.id, label: p.name }))}
                selected={projectIds}
                onToggle={(id) =>
                  setProjectIds((prev) =>
                    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                  )
                }
                emptyText={customerId ? 'Keine Projekte beim Kunden.' : 'Erst Kunde wählen.'}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Mitarbeiter (leer = alle)</Label>
              <MultiSelect
                items={users.map((u) => ({ id: u.id, label: u.name }))}
                selected={userIds}
                onToggle={(id) =>
                  setUserIds((prev) =>
                    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                  )
                }
                emptyText="Keine Mitarbeiter sichtbar."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Zeitraum</Label>
                <Select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as WorkflowPeriodStrategy)}
                >
                  {(Object.keys(PERIOD_LABELS) as WorkflowPeriodStrategy[]).map((k) => (
                    <option key={k} value={k}>
                      {PERIOD_LABELS[k]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>MwSt %</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={taxRatePercent}
                  onChange={(e) => setTaxRatePercent(e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5 pt-1">
              <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
                <input
                  type="checkbox"
                  checked={billableOnly}
                  onChange={(e) => setBillableOnly(e.target.checked)}
                  className="accent-accent h-4 w-4"
                />
                Nur abrechenbare Einträge
              </label>
              <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
                <input
                  type="checkbox"
                  checked={excludeAlreadyBilled}
                  onChange={(e) => setExcludeAlreadyBilled(e.target.checked)}
                  className="accent-accent h-4 w-4"
                />
                Bereits abgerechnete ausschließen
              </label>
            </div>
          </div>
        </section>
      </form>
    </SidePanel>
  )
}

function MultiSelect({
  items,
  selected,
  onToggle,
  emptyText,
}: {
  items: Array<{ id: string; label: string }>
  selected: string[]
  onToggle: (id: string) => void
  emptyText: string
}) {
  if (items.length === 0) {
    return <p className="text-xs text-text-tertiary">{emptyText}</p>
  }
  return (
    <div className="flex flex-wrap gap-1.5 rounded-lg border border-border-default bg-bg-overlay/40 p-2 max-h-32 overflow-y-auto">
      {items.map((it) => {
        const on = selected.includes(it.id)
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => onToggle(it.id)}
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs transition-colors',
              on
                ? 'bg-accent text-bg-base'
                : 'bg-bg-overlay text-text-secondary hover:bg-bg-overlay/80',
            )}
          >
            {on && <Check className="h-3 w-3" />}
            {it.label}
          </button>
        )
      })}
    </div>
  )
}

// ============================================================================
// Helpers
// ============================================================================

function scheduleLabel(kind: WorkflowScheduleKind): string {
  switch (kind) {
    case 'manual':
      return 'Manuell'
    case 'daily':
      return 'Täglich'
    case 'weekly':
      return 'Wöchentlich'
    case 'monthly':
      return 'Monatlich'
    case 'yearly':
      return 'Jährlich'
  }
}

function describeSchedule(kind: WorkflowScheduleKind, cfg: WorkflowScheduleConfig): string {
  const time = cfg.time ?? '09:00'
  switch (kind) {
    case 'manual':
      return 'Manuell — nur per Button'
    case 'daily':
      return `Täglich um ${time}`
    case 'weekly':
      return `Jeden ${WEEKDAYS[cfg.weekday ?? 1]} um ${time}`
    case 'monthly':
      return `Monatlich am ${cfg.dayOfMonth ?? 1}. um ${time}`
    case 'yearly':
      return `Jährlich am ${cfg.dayOfMonth ?? 1}. ${MONTHS[(cfg.month ?? 1) - 1]} um ${time}`
  }
}

function describeAction(actionType: string, cfg: Record<string, unknown>): string {
  if (actionType === 'create_invoice_from_time_entries') {
    const c = cfg as Partial<CreateInvoiceFromEntriesConfig>
    const period = c.period ? PERIOD_LABELS[c.period] : '—'
    const filters: string[] = []
    if (c.projectIds?.length) filters.push(`${c.projectIds.length} Projekte`)
    if (c.userIds?.length) filters.push(`${c.userIds.length} Mitarbeiter`)
    const filterStr = filters.length > 0 ? ` (${filters.join(', ')})` : ''
    return `Rechnungsentwurf aus Zeiteinträgen · ${period}${filterStr}`
  }
  return actionType
}

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
